import type { StyleRow, CustomFieldRowEntry } from './stylesFixtureData';
import { mandatoryExportCustomFieldDefinition } from './stylesBulkEditValidationFixtureData';

/**
 * Deterministic style fixtures for epic DL-66347 (D2 Playwright automation — dl-58912 "bulk edit
 * validation skips untouched fields but still fires on touched ones", Testmo folder
 * https://delogue.testmo.net/repositories/4?group_id=101500). Isolable via `?search=Dl58912Probe`.
 *
 * The ONLY new style fixture this epic needs beyond what the sibling DL-62595 epic's
 * `stylesBulkEditValidationFixtureData.ts` already seeded is a style pair carrying TWO
 * mandatory-for-export custom fields (dl-66362 / Testmo #888827) — validation that iterates
 * mandatory CFs and reports on the first, or accumulates across several, could pass with one CF
 * and fail with two, which no fixture in that sibling epic exercises. Every other story in this
 * epic reuses that epic's `bulkValidationProbeExportReadyA`/`B` (RFE=Yes, one mandatory CF
 * filled, WITH an existing supplier contact — see this epic's dl-66351/dl-66367 specs for why
 * that existing contact matters) or `bulkValidationProbeExportNotReadyA`/`B` directly rather than
 * duplicating them here.
 */

const FIXTURE_BRAND = { id: 29, userDefinedId: '1', name: 'Brand A' };
const FIXTURE_SUPPLIER = { id: 44, customId: 'S-44', name: 'Ningbo garment supplier' };
const FIXTURE_SEASON = { id: 26, customId: 'AW25', name: 'AW25', project: '' };
const FIXTURE_GROUP = { id: 71, customId: 'MEN', name: 'Menswear' };

/** The second mandatory-for-export CF this epic's multi-CF story needs — layered on top of the
 *  baseline plus `mandatoryExportCustomFieldDefinition` (id 90) via `apiTransform` on
 *  `/api/meta/styles/custom-fields`. */
export const secondMandatoryExportCustomFieldDefinition = {
  id: 91,
  name: 'Export Reference',
  position: 9,
  type: 2, // Text
  isMandatory: true,
  isSupplierEditable: true,
  internalOnly: false,
  maxChar: 255,
  isMultiLine: false,
};

const cfEntry = (id: number, value: string): CustomFieldRowEntry => ({
  id,
  type: 'text',
  value: { id: 0, value },
});

/** dl-66362 (Testmo #888827) — two mandatory CFs, both filled, on a Brand-only edit. No existing
 *  supplier contact (`contactPersonId: ''`) — Brand-only never touches Supplier, so the contact
 *  re-validation cascade documented in this epic's other Styles specs cannot apply regardless,
 *  but left contact-free for isolation. */
export const dl58912StyleMultiCfA: StyleRow = {
  id: 95850,
  name: 'Dl58912Probe MultiCf A',
  customId: 'ST-95850',
  description: null,
  logo: '',
  state: 'unpublished',
  season: FIXTURE_SEASON,
  group: FIXTURE_GROUP,
  categories: [],
  styleColors: [],
  properties: [],
  lastUpdated: '2026-01-10T09:00:00',
  brand: {
    id: FIXTURE_BRAND.id,
    customId: FIXTURE_BRAND.userDefinedId,
    name: FIXTURE_BRAND.name,
    contactPersonId: '',
    contactPersonName: '',
  },
  supplier: {
    id: FIXTURE_SUPPLIER.id,
    customId: FIXTURE_SUPPLIER.customId,
    name: FIXTURE_SUPPLIER.name,
    contactPersonId: '',
    contactPersonName: '',
  },
  customFields: [
    cfEntry(mandatoryExportCustomFieldDefinition.id, 'Multi CF1 note'),
    cfEntry(secondMandatoryExportCustomFieldDefinition.id, 'Multi CF2 ref'),
  ],
  readyForExport: true,
};
export const dl58912StyleMultiCfB: StyleRow = {
  ...dl58912StyleMultiCfA,
  id: 95851,
  name: 'Dl58912Probe MultiCf B',
  customId: 'ST-95851',
};

export const dl58912StylesFixtureData: StyleRow[] = [dl58912StyleMultiCfA, dl58912StyleMultiCfB];
